import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import { meetsRequiredRules } from '@eliadmin/core/src/components/nt/account/password/password-rules';
import { PASSWORD_RULES_ISSUE } from '@eliadmin/core/src/components/nt/account/password/password-schema';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

export type NewPasswordValues = { password: string; password_confirmation: string };

/**
 * The checks of a new password and its repeat – the „Moje konto“ rules (`passwordSchema`) without
 * the current password: the required admin rules (`PASSWORD_RULES_ISSUE` – the rule list under the
 * field shows them, `PasswordField` adds no error line), the core minimum of 8 characters when the
 * administration lists no rules, and the matching repeat. A form with more fields (registration)
 * adds the issues in its own `superRefine`.
 */
export function newPasswordIssues(
  values: NewPasswordValues,
  VM: FormValidationMessages,
  rulesText: string,
) {
  const issues: { path: keyof NewPasswordValues; message: string }[] = [];

  if (!values.password) {
    issues.push({ path: 'password', message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE() });
  } else if (!rulesText && values.password.length < 8) {
    issues.push({ path: 'password', message: VM.MIN_LENGTH_VALIDATION_MESSAGE(8) });
  } else if (!meetsRequiredRules(rulesText, values.password)) {
    issues.push({ path: 'password', message: PASSWORD_RULES_ISSUE });
  }

  if (!values.password_confirmation) {
    issues.push({
      path: 'password_confirmation',
      message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
    });
  } else if (values.password_confirmation !== values.password) {
    issues.push({
      path: 'password_confirmation',
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
    });
  }

  return issues;
}

/** Body of the core `resetPassword` without the token: the new password and its repeat. */
export const newPasswordSchema = (VM: FormValidationMessages, rulesText: string) =>
  __eliadmin_zod_0({ password: __eliadmin_zod_1(), password_confirmation: __eliadmin_zod_1() })
    .superRefine((values, ctx) => {
      for (const { path, message } of newPasswordIssues(values, VM, rulesText)) {
        ctx.addIssue({ code: 'custom', path: [path], message });
      }
    });
